import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CheckCheck, Send } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";

export default function MarkAttendance() {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [semesterId, setSemesterId] = useState("");
  const [periodId, setPeriodId] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const [topic, setTopic] = useState("");
  const [present, setPresent] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const { data: subjects = [] } = useQuery({
    queryKey: ["teacher-subjects"],
    queryFn: async () => (await api.get("/teacher/subjects")).data,
  });
  const semesters = [];
  subjects.forEach((s) => {
    if (!semesters.find((x) => x.id === s.semester_id)) {
      semesters.push({ id: s.semester_id, name: s.semester_name, department_id: s.department_id });
    }
  });
  const semester = semesters.find((x) => x.id === semesterId);
  const { data: periods = [] } = useQuery({
    queryKey: ["teacher-periods", semester?.department_id],
    queryFn: async () => (await api.get("/teacher/periods", { params: { department_id: semester.department_id } })).data,
    enabled: !!semester,
  });
  const semesterSubjects = subjects.filter((s) => s.semester_id === semesterId);
  const subject = semesterSubjects.find((s) => s.id === subjectId);
  const { data: roster = [] } = useQuery({
    queryKey: ["teacher-roster", subjectId, date],
    queryFn: async () => (await api.get("/teacher/roster", { params: { subject_id: subjectId, date } })).data,
    enabled: !!subjectId,
  });
  const presentCount = Object.values(present).filter(Boolean).length;
  const topicWords = topic.trim() ? topic.trim().split(/\s+/).length : 0;

  const toggle = (sid) => setPresent((p) => ({ ...p, [sid]: !p[sid] }));
  const markAll = () => {
    const all = {};
    roster.forEach((s) => { all[s.id] = true; });
    setPresent(all);
  };

  const submit = async () => {
    if (!date || !semesterId || !periodId || !subjectId) {
      toast.error("Select date, semester, period and subject first");
      return;
    }
    if (!topic.trim()) {
      toast.error("Topic taught is required");
      return;
    }
    if (topicWords > 20) {
      toast.error("Topic must be 20 words or fewer");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/teacher/attendance", {
        date, period_id: periodId, subject_id: subjectId, topic,
        present_ids: Object.keys(present).filter((id) => present[id]),
      });
      toast.success(`Submitted — ${data.present} present, ${data.absent} absent`);
      setPresent({});
      setTopic("");
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout title="Mark Attendance">
      <div className="bg-white border border-slate-200 rounded-md p-6 mb-6" data-testid="mark-filters">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-1.5">
            <Label>1. Date</Label>
            <Input type="date" data-testid="mark-date-input" value={date} max={today}
              onChange={(e) => setDate(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>2. Semester</Label>
            <Select value={semesterId} onValueChange={(v) => { setSemesterId(v); setPeriodId(""); setSubjectId(""); setPresent({}); }}>
              <SelectTrigger data-testid="mark-semester-select"><SelectValue placeholder="Select semester" /></SelectTrigger>
              <SelectContent>
                {semesters.map((s) => (
                  <SelectItem key={s.id} value={s.id} data-testid={`mark-semester-${s.id}`}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>3. Period</Label>
            <Select value={periodId} onValueChange={setPeriodId} disabled={!semesterId}>
              <SelectTrigger data-testid="mark-period-select"><SelectValue placeholder={semesterId ? "Select period" : "Pick semester first"} /></SelectTrigger>
              <SelectContent>
                {periods.map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`mark-period-${p.id}`}>
                    {p.label} ({p.start_time}–{p.end_time})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>4. Subject</Label>
            <Select value={subjectId} onValueChange={(v) => { setSubjectId(v); setPresent({}); }} disabled={!semesterId}>
              <SelectTrigger data-testid="mark-subject-select"><SelectValue placeholder={semesterId ? "Select subject" : "Pick semester first"} /></SelectTrigger>
              <SelectContent>
                {semesterSubjects.map((s) => (
                  <SelectItem key={s.id} value={s.id} data-testid={`mark-subject-${s.id}`}>
                    {s.name} ({s.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {subjectId && (
          <div className="space-y-1.5 mt-4">
            <Label>Topic taught <span className="text-red-600">*</span> <span className="text-slate-400 font-normal">(required, max 20 words)</span></Label>
            <Textarea data-testid="mark-topic-input" rows={2} maxLength={300}
              placeholder="e.g. Introduced limits and continuity with worked examples"
              value={topic} onChange={(e) => setTopic(e.target.value)} />
            <p className={`text-xs font-data ${topicWords > 20 ? "text-red-700" : "text-slate-400"}`} data-testid="topic-word-count">
              {topicWords}/20 words
            </p>
          </div>
        )}
      </div>

      {subjectId && (
        <div className="bg-white border border-slate-200 rounded-md" data-testid="roster-card">
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <div>
              <h2 className="font-heading text-lg font-semibold tracking-tight text-slate-950">
                {subject?.name} <span className="text-slate-400 font-normal">· {subject?.semester_name}</span>
              </h2>
              <p className="text-sm text-slate-500 mt-0.5">
                <span className="font-data text-emerald-700">{presentCount}</span> present ·{" "}
                <span className="font-data text-red-700">{roster.length - presentCount}</span> will be marked absent
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={markAll} data-testid="mark-all-present-btn">
                <CheckCheck className="h-4 w-4" /> All present
              </Button>
              <Button size="sm" onClick={submit} disabled={submitting || roster.length === 0}
                data-testid="submit-attendance-btn" className="bg-slate-950 hover:bg-slate-800 text-white">
                <Send className="h-4 w-4" /> {submitting ? "Submitting…" : "Submit attendance"}
              </Button>
            </div>
          </div>
          {roster.length === 0 ? (
            <div className="p-10 text-center" data-testid="roster-empty">
              <p className="text-sm text-slate-400">No students enrolled in this semester yet.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Roll Number</TableHead>
                  <TableHead className="text-right">Mark Present</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {roster.map((s) => (
                  <TableRow key={s.id} data-testid={`roster-row-${s.id}`}
                    className={present[s.id] ? "bg-emerald-50/50" : ""}>
                    <TableCell className="font-medium">{s.name}</TableCell>
                    <TableCell className="font-data">{s.roll_number}</TableCell>
                    <TableCell className="text-right">
                      <button onClick={() => toggle(s.id)} data-testid={`toggle-present-${s.id}`}
                        className={`inline-flex items-center rounded-md border px-3 py-1 text-xs font-semibold active:scale-90 transition-transform duration-150 ${
                          present[s.id]
                            ? "bg-emerald-600 text-white border-emerald-600"
                            : "bg-white text-slate-500 border-slate-300 hover:border-slate-950 hover:text-slate-950"
                        }`}>
                        {present[s.id] ? "Present" : "Mark present"}
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      )}

      {!subjectId && (
        <div className="border border-dashed border-slate-300 rounded-md p-16 text-center bg-white" data-testid="mark-empty-state">
          <p className="text-slate-500">Choose a date, semester, period and subject above to load the class roster.</p>
          <p className="text-sm text-slate-400 mt-1">Unmarked students are automatically recorded as absent on submit.</p>
        </div>
      )}
    </Layout>
  );
}
